import { OrcamentoIcon } from '../components/OrcamentoUI';
import LiquidText, { liquidTone } from '../components/LiquidText';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { recarregarPrazosSgi } from '../context/PrazosSgiContext';
import { ResolverPrazoModal, DefinirPrazoModal } from '../components/ResolverPrazo';

/* ============================================================
   PRAZOS DO SISTEMA INTEGRADO
   Tudo o que tem data para ser feito dentro do SGI e ainda não
   foi: ações das NC, medições dos objetivos, comunicações do plano,
   verificações de RMM e de obra, formação, auditorias, pontos da
   lista da auditora… A auditoria interna de setembro de 2026
   concluiu que estes prazos ficavam órfãos — só se dava pelo
   atraso quando alguém abria o assunto.

   Mostra os atrasados, os que vencem dentro do aviso prévio e os
   que deviam ter data e não têm. Os alertas ficam na aplicação:
   não se envia nada a ninguém. Dados de /sistema-integrado/prazos,
   calculados a cada pedido a partir de cada separador.

   Cada linha pode trazer "✓ Resolver" (data da resolução e evidência)
   e, nas que não têm data, "Definir prazo" — sem ter de ir ao
   separador. Quem grava é o módulo de origem, no sítio onde se
   gravaria indo lá (ver components/ResolverPrazo.jsx). Uma linha sem
   item.resolver nem item.definir_prazo fica só com "Abrir →".
   ============================================================ */

const OPCOES_AVISO = [30, 60, 90];

// Paleta de estado da aplicação; o ícone e o rótulo acompanham sempre a cor.
const ESTADOS = {
    atrasado: { label: 'Atrasado', icone: '!', cor: '#d03b3b', iconeTexto: '#fff', fundo: '#fdeeee', borda: '#f3c4c4', texto: '#9b2c2c' },
    a_vencer: { label: 'A vencer', icone: '◷', cor: '#fab219', iconeTexto: '#3b2a00', fundo: '#fff8e6', borda: '#fde1a0', texto: '#8a5a00' },
    sem_prazo: { label: 'Sem prazo', icone: '?', cor: '#ec835a', iconeTexto: '#fff', fundo: '#fff1ea', borda: '#f8cdb8', texto: '#9a4a26' },
};
const BOM = { icone: '✓', cor: '#0ca30c', iconeTexto: '#fff', fundo: '#ecfdf3', borda: '#bbf7d0', texto: '#166534' };

const fmtData = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—');
// "🦺 Segurança (SST)" → "Segurança (SST)": o ícone já está no separador.
const semIconeLabel = (s) => String(s || '').replace(/\n/g, ' ').replace(/^[^A-Za-zÀ-ÿ]+/, '');
const quando = (dias) => {
    if (dias === null || dias === undefined) return 'sem data';
    if (dias === 0) return 'vence hoje';
    if (dias < 0) return dias === -1 ? 'há 1 dia' : `há ${-dias} dias`;
    return dias === 1 ? 'falta 1 dia' : `faltam ${dias} dias`;
};

const Icone = ({ e, tamanho = 16 }) => (
    <span className="sgi-deadline-icon" aria-hidden="true" style={{
        display: 'inline-grid', placeItems: 'center', width: tamanho, height: tamanho, borderRadius: '50%',
        background: e.cor, color: e.iconeTexto, fontSize: tamanho * 0.62, fontWeight: 800, lineHeight: 1, flexShrink: 0,
    }}><OrcamentoIcon name={e.icone === '✓' ? 'check' : e.icone === '◷' ? 'clock' : e.icone === '!' ? 'warning' : 'info'} fallback={e.icone} /></span>
);

const ChipEstado = ({ estado }) => {
    const e = ESTADOS[estado] || ESTADOS.sem_prazo;
    return (
        <span data-liquid-tone={liquidTone(e.fundo)} data-liquid-badge="true" style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
            background: e.fundo, color: e.texto, border: `1px solid ${e.borda}`,
            padding: '2px 9px 2px 4px', borderRadius: 999, fontSize: '0.76rem', fontWeight: 700,
        }}>
            <Icone e={e} tamanho={15} /><LiquidText>{e.label}</LiquidText>
        </span>
    );
};

const lbl = { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 };
const inp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', background: '#fff' };
const btnSec = { border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', color: '#334155' };
// Resolver fecha o assunto: tem a cor do "em dia". Definir prazo só aparece nas
// linhas sem data e leva a cor delas.
const btnResolver = { ...btnSec, border: '1px solid #86efac', background: BOM.fundo, color: BOM.texto, fontWeight: 700 };
const btnPrazo = { ...btnSec, border: `1px solid ${ESTADOS.sem_prazo.borda}`, background: ESTADOS.sem_prazo.fundo, color: ESTADOS.sem_prazo.texto, fontWeight: 600 };
const btnLinha = { fontSize: '0.78rem', padding: '5px 12px', whiteSpace: 'nowrap' };

/* areaFixa: o painel vive dentro de uma área e mostra só o que é dela. A caixa
   de escolha da área desaparece — a área já é o separador onde se está. */
const PainelPrazos = ({ areas = [], areaFixa = '', onAbrir }) => {
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');
    const [area, setArea] = useState(areaFixa || 'todas');
    const [separador, setSeparador] = useState('todos');
    const [estadoFiltro, setEstadoFiltro] = useState('');
    const [pesquisa, setPesquisa] = useState('');
    const [aGravar, setAGravar] = useState(false);
    const [msgAviso, setMsgAviso] = useState('');
    // A janela aberta guarda o item como estava ao abrir: a lista pode
    // recarregar por trás dela sem lhe mudar o conteúdo.
    const [aResolver, setAResolver] = useState(null);
    const [aDefinir, setADefinir] = useState(null);
    const [msgFeito, setMsgFeito] = useState('');
    const temporizador = useRef(null);
    const faixaFeito = useRef(null);
    const focarFaixa = useRef(false);

    useEffect(() => () => clearTimeout(temporizador.current), []);

    const carregar = useCallback(async () => {
        setLoading(true); setErro('');
        try {
            const r = await api.get('/sistema-integrado/prazos');
            setDados(r.data);
        } catch (e) {
            console.error('Erro ao carregar prazos do SGI:', e);
            setErro(e.response?.data?.error || 'Não foi possível carregar os prazos.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    // Trocar de área troca de separador sem desmontar o painel: o filtro tem de
    // acompanhar, senão ficava a mostrar a área anterior.
    useEffect(() => {
        if (areaFixa) { setArea(areaFixa); setSeparador('todos'); setEstadoFiltro(''); }
    }, [areaFixa]);

    const atualizar = () => { carregar(); recarregarPrazosSgi(); };

    /* Depois de resolver ou de definir um prazo: a mensagem do servidor fica
       uns segundos no topo e voltam a pedir-se o painel e os números dos
       separadores. O forçar é porque uma contagem já a caminho foi calculada
       antes da gravação. */
    const concluido = (mensagem) => {
        setAResolver(null); setADefinir(null);
        setMsgFeito(mensagem);
        clearTimeout(temporizador.current);
        temporizador.current = setTimeout(() => setMsgFeito(''), 8000);
        recarregarPrazosSgi(true);
        focarFaixa.current = true;
        carregar();
    };

    // A linha resolvida sai da lista e leva o botão que tinha o foco: quem usa
    // o teclado fica na mensagem, e não no início da página. Só se sabe depois
    // de a lista nova estar desenhada — daí o efeito, e não logo a seguir ao
    // pedido.
    useEffect(() => {
        if (!focarFaixa.current || loading) return;
        focarFaixa.current = false;
        if (!document.activeElement || document.activeElement === document.body) {
            faixaFeito.current?.focus({ preventScroll: true });
        }
    }, [dados, erro, loading]);

    // O aviso prévio é um só para toda a gente: grava-se e recarrega-se o painel
    // e os números dos separadores, que dependem dele.
    const mudarAviso = async (dias) => {
        if (!dados || dias === dados.aviso || aGravar) return;
        setAGravar(true); setMsgAviso('');
        try {
            await api.put('/sistema-integrado/prazos/config', { dias });
            recarregarPrazosSgi(true);
            await carregar();
            setMsgAviso(`✓ Guardado — aviso com ${dias} dias de antecedência.`);
            setTimeout(() => setMsgAviso(''), 3000);
        } catch (e) {
            setMsgAviso(e.response?.data?.error || 'Não foi possível guardar o aviso.');
        } finally {
            setAGravar(false);
        }
    };

    const aviso = dados?.aviso ?? 30;
    const itens = dados?.itens || [];

    // Ordem e nomes dos separadores iguais aos botões do Sistema Integrado.
    const ordem = areas.flatMap(a => a.tabs.map(([k]) => k));
    const areaDe = Object.fromEntries(areas.flatMap(a => a.tabs.map(([k]) => [k, a.key])));
    const comItens = [...new Set(itens.map(i => i.separador))]
        .sort((a, b) => (ordem.indexOf(a) + 1 || 999) - (ordem.indexOf(b) + 1 || 999));
    const nomeSeparador = (k) => itens.find(i => i.separador === k)?.separador_label || k;

    const termo = pesquisa.trim().toLowerCase();
    const combina = (i) => !termo || [i.descricao, i.tipo, i.ref, i.responsavel, i.detalhe, i.separador_label]
        .some(t => String(t || '').toLowerCase().includes(termo));
    // Sem o filtro de separador: é daqui que saem os números escritos ao lado de
    // cada separador na lista de escolha. Vinham da contagem de tudo, e com uma
    // pesquisa a cortar a lista diziam 32 onde se viam dois.
    const noAmbitoSemSeparador = itens.filter(i => (area === 'todas' || i.area === area) && combina(i));
    // Tudo menos o estado: é o que os cartões contam, para servirem de filtro.
    const noAmbito = noAmbitoSemSeparador.filter(i => separador === 'todos' || i.separador === separador);
    const visiveis = estadoFiltro ? noAmbito.filter(i => i.estado === estadoFiltro) : noAmbito;
    const conta = (e) => noAmbito.filter(i => i.estado === e).length;

    const grupos = comItens
        .map(k => ({ key: k, itens: visiveis.filter(i => i.separador === k) }))
        .filter(g => g.itens.length > 0);

    // O separador escolhido fica na lista mesmo que a pesquisa o esvazie, para o
    // campo não aparecer em branco.
    const separadoresDaArea = comItens.filter(k =>
        (area === 'todas' || (areaDe[k] || itens.find(i => i.separador === k)?.area) === area)
        && (k === separador || noAmbitoSemSeparador.some(i => i.separador === k)));
    const contaSeparador = (k) => noAmbitoSemSeparador.reduce((c, i) => {
        if (i.separador === k) c[i.estado] += 1;
        return c;
    }, { atrasado: 0, a_vencer: 0, sem_prazo: 0 });

    const itensDaArea = itens.filter(i => area === 'todas' || i.area === area);
    const outrasAreas = areas.filter(a => a.key !== area).map(a => ({ ...a, total: itens.filter(i => i.area === a.key).length })).filter(a => a.total > 0);

    const cartoes = [
        { k: 'atrasado', label: 'Atrasados' },
        { k: 'a_vencer', label: `A vencer em ≤ ${aviso} dias` },
        { k: 'sem_prazo', label: 'Sem prazo definido' },
    ];

    return (
        <div className="sgi-deadlines">
            {/* Resultado da última gravação. Colada ao topo enquanto se percorre
                a lista, para se ver mesmo que a linha estivesse lá em baixo. A
                caixa existe sempre para o leitor de ecrã anunciar a mensagem. */}
            <div role="status" style={{ position: 'sticky', top: 8, zIndex: 5 }}>
                {msgFeito && (
                    <div data-liquid-notice="true" data-liquid-tone="success" ref={faixaFeito} tabIndex={-1} style={{
                        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '10px 14px', borderRadius: 10,
                        background: BOM.fundo, border: `1px solid ${BOM.borda}`, color: BOM.texto, fontSize: '0.88rem', fontWeight: 600,
                        boxShadow: '0 4px 14px rgba(15,23,42,0.10)', outline: 'none',
                    }}>
                        <Icone e={BOM} tamanho={16} />
                        <span style={{ flex: 1 }}>{msgFeito}</span>
                        <button onClick={() => setMsgFeito('')} aria-label="Fechar mensagem"
                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: BOM.texto, fontSize: '1rem', padding: '0 2px' }}><LiquidText>✕</LiquidText></button>
                    </div>
                )}
            </div>

            {/* Aviso prévio */}
            <div data-liquid-notice="true" data-liquid-tone="info" className="sgi-deadlines-intro" style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '10px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ fontSize: '0.88rem', color: '#3730a3', maxWidth: 620 }}><LiquidText>
                        ⏰ </LiquidText><strong>Validades{areaFixa ? ` — ${semIconeLabel((areas.find(a => a.key === areaFixa) || {}).label)}` : ' do Sistema Integrado'}</strong>
                        {' '}— prazos em atraso, sem data ou a vencer nos próximos {aviso} dias, nesta área.
                        As validades posteriores ficam nos respetivos separadores.
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}
                        title="A antecedência fica guardada e é a mesma para todos os utilizadores">
                        <span style={{ fontSize: '0.82rem', color: '#3730a3', marginRight: 2 }}>Avisar com</span>
                        <div className="sgi-deadline-periods" role="group" aria-label="Antecedência dos avisos">{OPCOES_AVISO.map(d => {
                            const ativo = aviso === d;
                            return (
                                <button key={d} onClick={() => mudarAviso(d)} disabled={aGravar || !dados}
                                    aria-pressed={ativo}
                                    style={{
                                        padding: '5px 12px', borderRadius: 999, fontSize: '0.8rem', cursor: aGravar ? 'wait' : 'pointer',
                                        border: `1px solid ${ativo ? '#4338ca' : '#c7d2fe'}`,
                                        background: ativo ? '#4338ca' : '#fff', color: ativo ? '#fff' : '#4338ca',
                                        fontWeight: ativo ? 700 : 500, opacity: aGravar && !ativo ? 0.6 : 1,
                                    }}>
                                    {d} dias
                                </button>
                            );
                        })}</div>
                        <span style={{ fontSize: '0.82rem', color: '#3730a3', marginLeft: 2 }}>de antecedência</span>
                        <button data-liquid-secondary="true" onClick={atualizar} style={{ ...btnSec, fontSize: '0.78rem', padding: '5px 12px', marginLeft: 6 }}><LiquidText>↻ Atualizar</LiquidText></button>
                    </div>
                </div>
                {/* Dito à vista e não só ao passar o rato: quem muda aqui muda
                    o painel de toda a gente. */}
                <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#4f46e5' }}>
                    A antecedência fica guardada e é a mesma para todos os utilizadores.
                </div>
                {msgAviso && <div style={{ marginTop: 8, fontSize: '0.82rem', color: msgAviso.startsWith('✓') ? '#166534' : '#991b1b' }}>{msgAviso}</div>}
            </div>

            {dados?.fontes_com_erro?.length > 0 && (
                <div data-liquid-notice="true" data-liquid-tone="warning" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: ESTADOS.sem_prazo.fundo, border: `1px solid ${ESTADOS.sem_prazo.borda}`, color: ESTADOS.sem_prazo.texto, borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '0.85rem' }}>
                    <span aria-hidden="true"><LiquidText>⚠</LiquidText></span>
                    <span>
                        <strong>Lista incompleta.</strong> Não foi possível ler: {dados.fontes_com_erro.map(f => f.fonte).join(', ')}.
                        O que vem daí não aparece abaixo.
                    </span>
                </div>
            )}

            {/* Cartões-resumo — clicar num mostra só esses */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                {cartoes.map(({ k, label }) => {
                    const n = conta(k);
                    const e = k === 'atrasado' && n === 0 ? { ...BOM } : ESTADOS[k];
                    const ativo = estadoFiltro === k;
                    return (
                        <button className="sgi-deadline-card" data-liquid-tone={k === 'atrasado' ? (n ? 'danger' : 'success') : 'warning'} key={k} onClick={() => setEstadoFiltro(f => (f === k ? '' : k))} aria-pressed={ativo}
                            title={ativo ? 'Mostrar todos os estados' : 'Mostrar só estes'}
                            style={{
                                flex: '1 1 160px', background: e.fundo, border: `1px solid ${e.borda}`, borderRadius: 10,
                                padding: '12px 16px', textAlign: 'center', cursor: 'pointer', fontFamily: 'inherit',
                                outline: ativo ? `2px solid ${e.cor}` : 'none', outlineOffset: 1,
                            }}>
                            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: e.texto }}>{loading && !dados ? '…' : n}</div>
                            <div style={{ fontSize: '0.82rem', color: e.texto, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Icone e={e} tamanho={15} /><LiquidText>{label}</LiquidText>
                            </div>
                        </button>
                    );
                })}
            </div>
            <div style={{ fontSize: '0.74rem', color: '#94a3b8', marginBottom: 14 }}>
                {estadoFiltro ? `A mostrar só “${ESTADOS[estadoFiltro].label}” — clique outra vez no cartão para ver todos.` : 'Clique num cartão para ver só esses.'}
            </div>

            {/* Filtros */}
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
                <div hidden={!!areaFixa}>
                    <label data-liquid-label="true" style={lbl}>Área</label>
                    <select value={area} style={inp}
                        onChange={e => {
                            const nova = e.target.value;
                            setArea(nova);
                            if (nova !== 'todas' && separador !== 'todos' && (areaDe[separador] || '') !== nova) setSeparador('todos');
                        }}>
                        <option value="todas">Todas</option>
                        {areas.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                    </select>
                </div>
                <div>
                    <label data-liquid-label="true" style={lbl}>Separador</label>
                    <select value={separador} onChange={e => setSeparador(e.target.value)} style={{ ...inp, maxWidth: 320 }}>
                        <option value="todos">Todos</option>
                        {separadoresDaArea.map(k => {
                            const c = contaSeparador(k);
                            const partes = [c.atrasado ? `${c.atrasado} atrasado(s)` : '', c.a_vencer ? `${c.a_vencer} a vencer` : '', c.sem_prazo ? `${c.sem_prazo} sem prazo` : ''].filter(Boolean);
                            return <option key={k} value={k}>{nomeSeparador(k)}{partes.length ? ` — ${partes.join(', ')}` : ''}</option>;
                        })}
                    </select>
                </div>
                <div style={{ flex: '1 1 220px' }}>
                    <label data-liquid-label="true" style={lbl}>Pesquisar</label>
                    <input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} placeholder="Código, assunto, responsável…"
                        value={pesquisa} onChange={e => setPesquisa(e.target.value)} />
                </div>
            </div>

            {/* Lista agrupada por separador */}
            {loading && !dados ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>A carregar prazos…</div>
            ) : erro ? (
                <div data-liquid-notice="true" data-liquid-tone={liquidTone('#fef2f2')} style={{ padding: 20, textAlign: 'center', color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10 }}>{erro}</div>
            ) : itens.length === 0 ? (
                <div data-liquid-notice="true" data-liquid-tone="success" style={{ padding: 30, textAlign: 'center', color: BOM.texto, background: BOM.fundo, border: `1px solid ${BOM.borda}`, borderRadius: 10 }}><LiquidText>
                    ✅ Nada em atraso nem a vencer nos próximos </LiquidText>{aviso} dias.
                </div>
            ) : grupos.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                    <strong>{itensDaArea.length === 0 ? `Sem prazos em atraso, sem data ou a vencer nos próximos ${aviso} dias nesta área.` : 'Nenhum prazo com estes filtros.'}</strong>
                    {itensDaArea.length === 0 && outrasAreas.length > 0 && <>
                        <p>Existem prazos nas outras áreas do Sistema Integrado:</p>
                        <div className="sgi-deadlines-other-areas">{outrasAreas.map(a => <button key={a.key} data-liquid-secondary="true" onClick={() => onAbrir?.(`validades_${a.key}`)}><LiquidText>{a.label}</LiquidText> <span data-liquid-badge="true" data-liquid-tone="info">{a.total}</span></button>)}</div>
                    </>}
                    {itensDaArea.length > 0 && <button data-liquid-secondary="true" onClick={() => { setPesquisa(''); setSeparador('todos'); setEstadoFiltro(''); }} style={{ marginTop: 12 }}>Limpar filtros</button>}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: loading ? 0.6 : 1 }}>
                    {grupos.map(g => {
                        const primeiro = g.itens[0];
                        const n = (e) => g.itens.filter(i => i.estado === e).length;
                        return (
                            <section key={g.key} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                                <header style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <strong style={{ fontSize: '0.95rem', color: '#1e293b' }}>{primeiro.separador_label}</strong>
                                    <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>{primeiro.area_label}</span>
                                    <span style={{ display: 'inline-flex', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
                                        {['atrasado', 'a_vencer', 'sem_prazo'].filter(e => n(e) > 0).map(e => (
                                            <span key={e} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.76rem', fontWeight: 700, color: ESTADOS[e].texto }}>
                                                <Icone e={ESTADOS[e]} tamanho={14} />{n(e)} {ESTADOS[e].label.toLowerCase()}
                                            </span>
                                        ))}
                                    </span>
                                </header>
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="standard-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                                        <thead>
                                            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0', color: '#475569' }}>
                                                <th style={{ padding: '8px 10px' }}>Estado</th>
                                                <th style={{ padding: '8px 10px' }}>Prazo</th>
                                                <th style={{ padding: '8px 10px' }}>O quê</th>
                                                <th style={{ padding: '8px 10px' }}>Responsável</th>
                                                <th style={{ padding: '8px 10px' }}>Detalhe</th>
                                                <th style={{ padding: '8px 10px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {g.itens.map((i, idx) => (
                                                <tr key={`${i.tipo}-${i.ref}-${idx}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '8px 10px', verticalAlign: 'top' }}><ChipEstado estado={i.estado} /></td>
                                                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                                                        <div style={{ fontWeight: 600 }}>{fmtData(i.prazo)}</div>
                                                        <div style={{ fontSize: '0.74rem', color: ESTADOS[i.estado]?.texto || '#64748b' }}>{quando(i.dias)}</div>
                                                    </td>
                                                    <td style={{ padding: '8px 10px', verticalAlign: 'top', minWidth: 220 }}>
                                                        <div style={{ fontSize: '0.74rem', color: '#64748b' }}>{i.tipo}{i.ref ? ` · ${i.ref}` : ''}</div>
                                                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{i.descricao || '—'}</div>
                                                    </td>
                                                    <td style={{ padding: '8px 10px', verticalAlign: 'top', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{i.responsavel || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                                    <td style={{ padding: '8px 10px', verticalAlign: 'top', color: '#64748b', fontSize: '0.82rem', minWidth: 200 }}>{i.detalhe || '—'}</td>
                                                    <td style={{ padding: '8px 10px', verticalAlign: 'top', textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                            {i.resolver && (
                                                                <button onClick={() => setAResolver(i)} title={i.resolver.rotulo}
                                                                    aria-label={`Resolver: ${i.resolver.rotulo}${i.ref ? ` (${i.ref})` : ''}`}
                                                                    style={{ ...btnResolver, ...btnLinha }}><LiquidText>
                                                                    ✓ Resolver
                                                                </LiquidText></button>
                                                            )}
                                                            {i.definir_prazo && (
                                                                <button onClick={() => setADefinir(i)} title={i.definir_prazo.rotulo || 'Definir prazo'}
                                                                    aria-label={`Definir prazo${i.ref ? ` (${i.ref})` : ''}`}
                                                                    style={{ ...btnPrazo, ...btnLinha }}><LiquidText>
                                                                    📅 Definir prazo
                                                                </LiquidText></button>
                                                            )}
                                                            {onAbrir && (
                                                                <button data-liquid-secondary="true" onClick={() => onAbrir(i.separador)} title={`Abrir ${i.separador_label}`}
                                                                    style={{ ...btnSec, ...btnLinha }}>
                                                                    Abrir →
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        );
                    })}
                </div>
            )}

            {/* Um erro ao gravar recarrega a lista por trás da janela: o item
                pode ter sido tratado noutro posto entretanto. */}
            {aResolver && (
                <ResolverPrazoModal item={aResolver} etiquetaEstado={<ChipEstado estado={aResolver.estado} />}
                    onFechar={() => setAResolver(null)} onResolvido={concluido} onFalhou={carregar} />
            )}
            {aDefinir && (
                <DefinirPrazoModal item={aDefinir} etiquetaEstado={<ChipEstado estado={aDefinir.estado} />}
                    onFechar={() => setADefinir(null)} onDefinido={concluido} onFalhou={carregar} />
            )}
        </div>
    );
};

export default PainelPrazos;
